/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { BriefcaseIcon, HeartIcon, UserIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button as ButtonV2, Heading, IconButton, Tabs, TabItem } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

import { MainUserInformation, WorkerSkillsAndLanguage } from './candidate-profile';
import { WorkerPreferences } from './candidate-profile/worker-preferences';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ICandidateProfileSectionProps {}

const CandidateProfileSection: React.FunctionComponent<ICandidateProfileSectionProps> = (props) => {
  const tabs = useProfileTabs();
  // Non-sticky like master: the sticky tab list (fixed 88px offset + opaque bar) overlapped and clipped
  // the top of the scrolled CV content inside this modal. Let it scroll with the content instead.
  return <Tabs tabs={tabs} tabListClassName="lg:pt-4" />;
};

export default CandidateProfileSection;

const useProfileTabs = () => {
  const { t } = useTranslation('onboarding-page');
  const tabs: TabItem[] = [
    {
      label: t('basic_information'),
      content: <MainUserInformation />,
      icon: <UserIcon className="w-5 h-5" />,
    },
    {
      label: t('experiences'),
      content: <WorkerSkillsAndLanguage />,
      icon: <BriefcaseIcon className="w-5 h-5" />,
    },
    {
      label: t('preferences'),
      content: <WorkerPreferences />,
      icon: <HeartIcon className="w-5 h-5" />,
    },
  ];
  return tabs;
};

export const TabCardWrapper = ({
  children,
  title,
  onEdit,
  isEditMode,
  noBgColor,
}: {
  children: React.ReactNode;
  title?: string;
  onEdit?: () => void;
  isEditMode?: boolean;
  noBgColor?: boolean;
}) => {
  const { t } = useTranslation('common');

  return (
    <Card isEditMode={isEditMode} noBgColor={noBgColor} className={'px-4 py-6 lg:px-4 lg:py-6'}>
      <div className="flex box-border flex-row items-center justify-between w-full p-0">
        <Heading className="leading-7.5">{title}</Heading>
        {onEdit &&
          (isEditMode ? (
            <IconButton ariaLabel={t('close')} Icon={XMarkIcon} size="sm" onClick={onEdit} variant="restworld" />
          ) : (
            <ButtonV2 size="sm" variant="secondary-restworld" StartIcon={PencilSquareIcon} onClick={onEdit}>
              {t('edit')}
            </ButtonV2>
          ))}
      </div>
      {children}
    </Card>
  );
};
export const Card = ({
  isEditMode,
  noBgColor,
  className,
  ...props
}: { isEditMode?: boolean; noBgColor?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex box-border flex-col w-full rounded-3.75 lg:p-8 p-4',
      !isEditMode && 'overflow-hidden',
      noBgColor ? 'border border-neutral-40 rounded-2xl p-4 lg:py-5 px-4' : 'bg-primary-50',
      className
    )}
    {...props}
  />
);
